<template>
  <div
    class="drawer-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="responses-drawer-title"
    @click.self="emit('close')"
  >
    <aside
      class="drawer-panel"
      :class="{ expanded: selectedResponse }"
    >
      <!-- Header -->

      <header class="drawer-header">
        <div class="header-main">
          <div class="header-logo">
            <img
              src="../assets/hr3logo.png"
              alt="Hr3"
            />
          </div>

          <div class="header-content">
            <span class="header-label">
              Управление вакансией
            </span>

            <h2 id="responses-drawer-title">
              Отклики на вакансию
            </h2>

            <p>
              {{ vacancy?.title || "Название вакансии" }}
            </p>
          </div>
        </div>

        <button
          type="button"
          class="close-button"
          aria-label="Закрыть"
          @click="emit('close')"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 6L14 14M14 6L6 14"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </header>

      <!-- Content -->

      <main class="drawer-content">
        <!-- Loading -->

        <div
          v-if="isLoading"
          class="responses-loading"
        >
          <div class="content-heading">
            <div>
              <span class="section-label">
                Кандидаты
              </span>

              <h3>
                Загружаем отклики
              </h3>
            </div>

            <span class="loading-badge">
              <span class="small-loader"></span>
              Обновление
            </span>
          </div>

          <div class="responses-grid">
            <ResponseSkeleton
              v-for="index in 4"
              :key="index"
            />
          </div>
        </div>

        <!-- Empty -->

        <div
          v-else-if="!hasResponses"
          class="empty-responses"
        >
          <div class="empty-illustration">
            <div class="empty-glow"></div>

            <img
              src="../assets/empty-responses.png"
              alt=""
            />
          </div>

          <h3>
            Откликов пока нет
          </h3>

          <p>
            Новые кандидаты появятся здесь сразу после отклика
            на вакансию.
          </p>

          <div class="empty-note">
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="10"
                cy="10"
                r="8"
                stroke="currentColor"
                stroke-width="1.5"
              />

              <path
                d="M10 9V14M10 6.2V6.4"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>

            Мы уведомим вас, когда появится новый отклик.
          </div>
        </div>

        <!-- Responses -->

        <template v-else>
          <div class="content-heading">
            <div>
              <span class="section-label">
                Кандидаты
              </span>

              <h3>
                {{ responsesTitle }}
              </h3>
            </div>
          </div>

          <div
            class="responses-layout"
            :class="{ 'has-details': selectedResponse }"
          >
            <!-- List -->

            <section class="responses-column">
              <div class="responses-grid">
                <ResponseCard
                  v-for="response in responses"
                  :key="response.response_id"
                  :worker="response.worker"
                  :is-viewed="response.isViewed"
                  :class="{
                    selected:
                      selectedResponse?.response_id ===
                      response.response_id
                  }"
                  @click="selectResponse(response)"
                  @delete="deleteResponse(response.response_id)"
                  @chat="handleChatClick"
                />
              </div>
            </section>

            <!-- Candidate details -->

            <Transition name="details">
              <aside
                v-if="selectedResponse"
                class="worker-details"
              >
                <div class="details-header">
                  <div>
                    <span class="section-label">
                      Профиль кандидата
                    </span>

                    <h3>
                      Детали отклика
                    </h3>
                  </div>

                  <button
                    type="button"
                    class="details-close"
                    aria-label="Закрыть карточку кандидата"
                    @click="selectedResponse = null"
                  >
                    <svg
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M6 6L14 14M14 6L6 14"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                      />
                    </svg>
                  </button>
                </div>

                <div class="worker-card">
                  <div class="avatar-wrapper">
                    <img
                      :src="
                        selectedResponse.worker?.image_url ||
                        '/default_avatar.png'
                      "
                      :alt="
                        selectedResponse.worker?.name ||
                        'Кандидат'
                      "
                      class="avatar"
                    />

                    <span class="status-dot"></span>
                  </div>

                  <h3 class="worker-name">
                    {{
                      selectedResponse.worker?.name ||
                      "Имя не указано"
                    }}
                  </h3>

                  <p class="worker-title">
                    {{
                      selectedResponse.worker?.specifics ||
                      "Специализация не указана"
                    }}
                  </p>

                  <div class="worker-meta">
                    <span>
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M4 16V14.7C4 12.7 5.7 11 7.7 11H12.3C14.3 11 16 12.7 16 14.7V16"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                        />

                        <circle
                          cx="10"
                          cy="6.5"
                          r="3"
                          stroke="currentColor"
                          stroke-width="1.5"
                        />
                      </svg>

                      Кандидат Hr3
                    </span>

                    <span>
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M5 10L8.2 13.2L15 6.5"
                          stroke="currentColor"
                          stroke-width="1.7"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>

                      Отклик получен
                    </span>
                  </div>

                  <div class="action-buttons">
                    <button
                      type="button"
                      class="primary-action"
                      @click="
                        handleChatClick(
                          selectedResponse.worker
                        )
                      "
                    >
                      <img
                        src="../../public/chat-go.png"
                        alt=""
                      />

                      Написать
                    </button>

                    <button
                      type="button"
                      class="icon-action"
                      aria-label="Открыть профиль кандидата"
                      title="Открыть профиль"
                      @click="
                        openCandidateProfile(
                          selectedResponse.worker
                        )
                      "
                    >
                    <img
                        src="../../public/dd-profile-dark.png"
                        alt=""
                      />
                    </button>

                    <button
                      type="button"
                      class="icon-action menu-trigger"
                      @click.stop="showMenu = !showMenu"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <circle cx="12" cy="5" r="2"/>
                        <circle cx="12" cy="12" r="2"/>
                        <circle cx="12" cy="19" r="2"/>
                      </svg>

                      <Transition name="menu">
                        <div class="menu-wrapper">
                          <button
                            type="button"
                            class="icon-action"
                            @click.stop="showMenu = !showMenu"
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <circle cx="12" cy="5" r="2"/>
                              <circle cx="12" cy="12" r="2"/>
                              <circle cx="12" cy="19" r="2"/>
                            </svg>
                          </button>

                          <Transition name="menu">
                            <div
                              v-if="showMenu"
                              class="action-menu"
                            >
                              <button @click="handleChatClick(selectedResponse.worker)">
                                <div class="menu-icon">
                                  <img src="../../public/chat-go.png">
                                </div>

                                <div class="menu-text">
                                  <span>Написать</span>
                                  <small>Открыть диалог</small>
                                </div>
                              </button>

                              <button @click="openCandidateProfile(selectedResponse.worker)">
                                <div class="menu-icon">
                                  <img src="../../public/dd-profile-dark.png">
                                </div>

                                <div class="menu-text">
                                  <span>Профиль</span>
                                  <small>Посмотреть кандидата</small>
                                </div>
                              </button>

                              <div class="divider"></div>

                              <button
                                class="danger"
                                @click="deleteResponse(selectedResponse.response_id)"
                              >
                                <div class="menu-icon danger-icon">
                                  🗑
                                </div>

                                <div class="menu-text">
                                  <span>Удалить отклик</span>
                                  <small>Это действие необратимо</small>
                                </div>
                              </button>
                            </div>
                          </Transition>
                        </div>
                      
                      </Transition>
                    </button>
                  </div>
                </div>

                <SmartMatchCard
                  :smart-match="
                    selectedResponse.smartMatch
                  "
                />

                <div class="ai-analysis">
                  <div class="analysis-header">
                    <div class="analysis-icon">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M12 3L14 8L19 10L14 12L12 17L10 12L5 10L10 8L12 3Z"
                          stroke="currentColor"
                          stroke-width="1.6"
                          stroke-linejoin="round"
                        />

                        <path
                          d="M18 15L19 18L22 19L19 20L18 23L17 20L14 19L17 18L18 15Z"
                          stroke="currentColor"
                          stroke-width="1.4"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>

                    <div>
                      <span>
                        AI-анализ
                      </span>

                      <h4>
                        Аннотация отклика
                      </h4>
                    </div>
                  </div>

                  <div class="analysis-scroll">
                    <p>
                      {{
                        selectedResponse.annotation ||
                        "Аннотация для данного отклика пока отсутствует."
                      }}
                    </p>
                  </div>

                  <div class="openai-mark">
                    <span class="openai-dot"></span>
                    Powered by Ai
                  </div>
                </div>
              </aside>
            </Transition>
          </div>
        </template>
      </main>

      <footer class="drawer-footer">
        <button
          type="button"
          class="edit-btn"
          @click="editVacancy(vacancy)"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4 15.5V17H5.5L15 7.5C15.8 6.7 15.8 5.5 15 4.7L14.3 4C13.5 3.2 12.3 3.2 11.5 4L4 11.5V15.5Z"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linejoin="round"
            />
          </svg>

          Редактировать
        </button>

        <button
          type="button"
          class="delete-btn"
          @click="showDeleteModal = true"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 6V15C6 16.1 6.9 17 8 17H12C13.1 17 14 16.1 14 15V6"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />

            <path
              d="M4.5 6H15.5M8 6V4.5H12V6"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          Удалить
        </button>
      </footer>
    </aside>
  </div>

  <Snackbar
    :message="snackbarMessage"
    :type="snackbarType"
    :show="snackbarShow"
  />

  <DeleteOrFreezeModal
    :show="showDeleteModal"
    @close="showDeleteModal = false"
    @freeze="handleFreeze"
    @delete="handleDelete"
  />
</template>

<script setup>
import {
  computed,
  onMounted,
  ref
} from "vue";

import SmartMatchCard
  from "../components/SmartMatchCard.vue";

import { useRouter } from "vue-router";

import Snackbar
  from "../components/Snackbar.vue";

import ResponseCard
  from "../components/ResponseCard.vue";

import ResponseSkeleton
  from "../components/ResponseSkeleton.vue";

import DeleteOrFreezeModal
  from "../components/DeleteOrFreezeModal.vue";

import ResponsesService
  from "../api/ResponsesService";

import MyVacanciesService
  from "../api/MyVacanciesService";

const props = defineProps({
  vacancy: {
    type: Object,
    default: null
  }
});

const emit = defineEmits([
  "close",
  "updated"
]);

const router = useRouter();
const showMenu = ref(false);
const responses = ref([]);
const selectedResponse = ref(null);

const isLoading = ref(true);
const deletingResponseId = ref(null);
const showDeleteModal = ref(false);

const snackbarShow = ref(false);
const snackbarMessage = ref("");
const snackbarType = ref("success");

const vacancyId = computed(() => {
  return (
    props.vacancy?.id ||
    props.vacancy?._id ||
    props.vacancy?.vacancy_id ||
    ""
  );
});

const hasResponses = computed(() => {
  return responses.value.length > 0;
});

const responsesTitle = computed(() => {
  const count = responses.value.length;
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (
    lastDigit === 1 &&
    lastTwoDigits !== 11
  ) {
    return `${count} отклик на вакансию`;
  }

  if (
    [2, 3, 4].includes(lastDigit) &&
    ![12, 13, 14].includes(lastTwoDigits)
  ) {
    return `${count} отклика на вакансию`;
  }

  return `${count} откликов на вакансию`;
});

function showSnackbar(
  message,
  type = "success"
) {
  snackbarMessage.value = message;
  snackbarType.value = type;
  snackbarShow.value = false;

  window.setTimeout(() => {
    snackbarShow.value = true;
  }, 10);
}

async function loadResponses() {
  if (!vacancyId.value) {
    console.error(
      "❌ Не найден ID вакансии:",
      props.vacancy
    );

    isLoading.value = false;
    return;
  }

  isLoading.value = true;

  try {
    console.log(
      "📡 Загружаем отклики:",
      vacancyId.value
    );

    const result =
      await ResponsesService.getResponses(
        vacancyId.value
      );

    responses.value =
      Array.isArray(result)
        ? result
        : result?.responses || [];

    if (
      selectedResponse.value &&
      !responses.value.some(
        item =>
          item.response_id ===
          selectedResponse.value.response_id
      )
    ) {
      selectedResponse.value = null;
    }

    console.log(
      "✅ Отклики загружены:",
      responses.value
    );
  } catch (error) {
    handleRequestError(
      error,
      "Не удалось загрузить отклики"
    );

    responses.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function selectResponse(response) {
  showMenu.value = false;

  const isSame =
    selectedResponse.value?.response_id === response.response_id;

  // если кликнули по уже выбранному — просто закрываем детали
  if (isSame) {
    selectedResponse.value = null;
    return;
  }

  // выбираем
  selectedResponse.value = response;

  // ✅ отметим просмотр
  try {
    if (response?.response_id) {
      await ResponsesService.viewResponse(response.response_id);
      console.log("👁 viewResponse ok:", response.response_id);
    }
  } catch (e) {
    console.error("❌ viewResponse failed:", e);
  }
}

async function deleteResponse(responseId) {
  if (
    !responseId ||
    deletingResponseId.value
  ) {
    return;
  }

  const shouldDelete =
    window.confirm(
      "Удалить этот отклик?"
    );

  if (!shouldDelete) {
    return;
  }

  deletingResponseId.value = responseId;

  try {
    await ResponsesService.deleteResponse(
      responseId
    );

    responses.value =
      responses.value.filter(
        response =>
          response.response_id !== responseId
      );

    if (
      selectedResponse.value?.response_id ===
      responseId
    ) {
      selectedResponse.value = null;
    }

    showSnackbar(
      "Отклик удалён"
    );
  } catch (error) {
    handleRequestError(
      error,
      "Не удалось удалить отклик"
    );
  } finally {
    deletingResponseId.value = null;
  }
}

function editVacancy(vacancy) {
  if (!vacancy) {
    return;
  }

  const id =
    vacancy.id ||
    vacancy._id ||
    vacancy.vacancy_id;

  if (!id) {
    console.error(
      "❌ У вакансии нет ID:",
      vacancy
    );

    return;
  }

  localStorage.setItem(
    "vacancy_to_edit",
    JSON.stringify(vacancy)
  );

  emit("close");

  router.push(
    `/edit-my-vacancy/${id}`
  );
}

function handleChatClick(worker) {
  if (!worker?.user_id) {
    showSnackbar(
      "Не удалось определить пользователя",
      "error"
    );

    return;
  }

  emit("close");

  router.push({
    name: "Dialogs",

    query: {
      ghost_user_id:
        worker.user_id,

      name:
        worker.name || "",

      image:
        worker.image_url || "",

      specifics:
        worker.specifics || ""
    }
  });
}

function openCandidateProfile(worker) {
  const userId =
    worker?.user_id ||
    worker?.id ||
    worker?._id;

  if (!userId) {
    showSnackbar(
      "Не удалось определить профиль кандидата",
      "error"
    );

    return;
  }

  emit("close");

  router.push({
    path: `/user/${userId}`
  });
}

async function handleFreeze() {
  if (!vacancyId.value) {
    return;
  }

  try {
    await MyVacanciesService
      .freezeVacancy(
        vacancyId.value
      );

    showDeleteModal.value = false;

    showSnackbar(
      "Вакансия перемещена в архив"
    );

    emit("updated", {
      type: "freeze",
      vacancyId: vacancyId.value
    });

    window.setTimeout(() => {
      emit("close");
    }, 500);
  } catch (error) {
    handleRequestError(
      error,
      "Не удалось заморозить вакансию"
    );
  }
}

async function handleDelete() {
  if (!vacancyId.value) {
    return;
  }

  try {
    await MyVacanciesService
      .deleteVacancy(
        vacancyId.value
      );

    showDeleteModal.value = false;

    showSnackbar(
      "Вакансия удалена"
    );

    emit("updated", {
      type: "delete",
      vacancyId: vacancyId.value
    });

    window.setTimeout(() => {
      emit("close");
    }, 500);
  } catch (error) {
    handleRequestError(
      error,
      "Не удалось удалить вакансию"
    );
  }
}

function handleRequestError(
  error,
  fallbackMessage
) {
  const status =
    error.response?.status;

  console.error(
    "❌ Ошибка запроса:",
    {
      status,
      data:
        error.response?.data,
      message:
        error.message
    }
  );

  if (status === 401) {
    showSnackbar(
      "Сессия истекла. Авторизуйтесь повторно",
      "error"
    );

    return;
  }

  if (status === 403) {
    showSnackbar(
      "У вас нет доступа к этой вакансии",
      "error"
    );

    return;
  }

  showSnackbar(
    fallbackMessage,
    "error"
  );
}

onMounted(() => {
  loadResponses();
});
</script>
<style scoped>
* {
  box-sizing: border-box;
}

/* =========================================================
   THEME VARIABLES
========================================================= */

.drawer-overlay {
  --drawer-bg: #ffffff;
  --drawer-surface: #ffffff;
  --drawer-surface-soft: #f7f8fa;
  --drawer-surface-muted: #f1f3f6;

  --drawer-text: #242932;
  --drawer-text-secondary: #7c8491;
  --drawer-text-muted: #9299a5;

  --drawer-border: rgba(24, 34, 55, 0.08);
  --drawer-border-strong: #e2e6ec;

  --drawer-blue: #3d82ed;
  --drawer-blue-dark: #4d68e7;
  --drawer-danger: #d84f55;

  --drawer-scrollbar: rgba(99, 108, 123, 0.3);
  --drawer-shadow: rgba(0, 0, 0, 0.3);

  position: fixed;
  inset: 0;
  z-index: 2000;

  display: flex;
  align-items: stretch;
  justify-content: flex-start;

  padding: 12px;

  background: rgba(10, 13, 20, 0.58);

  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  animation: overlay-appear 0.2s ease;
}

[data-theme="dark"] .drawer-overlay {
  --drawer-bg: #1c1c1f;
  --drawer-surface: #29292d;
  --drawer-surface-soft: #242428;
  --drawer-surface-muted: #303035;

  --drawer-text: #f1f2f4;
  --drawer-text-secondary: #9da3ad;
  --drawer-text-muted: #747b86;

  --drawer-border: rgba(255, 255, 255, 0.07);
  --drawer-border-strong: #3b3c42;

  --drawer-scrollbar: rgba(185, 192, 205, 0.3);
  --drawer-shadow: rgba(0, 0, 0, 0.48);
}

/* =========================================================
   DRAWER
========================================================= */

.drawer-panel {
  width: min(900px, calc(100vw - 24px));
  height: calc(100dvh - 24px);

  display: flex;
  flex-direction: column;

  overflow: hidden;

  color: var(--drawer-text);
  background: var(--drawer-bg);

  border: 1px solid var(--drawer-border);
  border-radius: 24px;

  box-shadow:
    0 30px 90px var(--drawer-shadow);

  transition:
    width 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  animation:
    drawer-appear 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.drawer-panel.expanded {
  width: min(1260px, calc(100vw - 24px));
}

/* =========================================================
   HEADER
========================================================= */

.drawer-header {
  min-height: 90px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  flex-shrink: 0;

  padding: 18px 24px;

  background:
    radial-gradient(
      circle at 10% 0%,
      rgba(55, 132, 247, 0.09),
      transparent 220px
    ),
    var(--drawer-bg);

  border-bottom: 1px solid var(--drawer-border);
}

.header-main {
  min-width: 0;

  display: flex;
  align-items: center;
  gap: 14px;
}

.header-logo {
  width: 48px;
  height: 48px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  background:
    linear-gradient(
      145deg,
      #36a1ff,
      #4078f3 55%,
      #625bea
    );

  border-radius: 15px;

  box-shadow:
    0 8px 20px rgba(48, 126, 246, 0.23);
}

.header-logo img {
  width: 27px;
  height: auto;

  object-fit: contain;

  filter: brightness(0) invert(1);
}

.header-content {
  min-width: 0;
}

.header-label,
.section-label {
  display: block;

  color: var(--drawer-text-muted);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.header-content h2 {
  margin: 3px 0 0;

  color: var(--drawer-text);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.25;
}

.header-content p {
  max-width: 620px;
  margin: 4px 0 0;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  color: var(--drawer-text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
}

.close-button,
.details-close {
  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  padding: 0;
  cursor: pointer;

  color: var(--drawer-text-secondary);
  background: var(--drawer-surface-muted);

  border: none;
  border-radius: 50%;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.close-button {
  width: 38px;
  height: 38px;
}

.close-button svg {
  width: 19px;
  height: 19px;
}

.close-button:hover,
.details-close:hover {
  color: var(--drawer-text);
  background: var(--drawer-border-strong);

  transform: rotate(5deg);
}

.drawer-content {
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 26px;
}

.content-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  flex-shrink: 0;

  margin-bottom: 17px;
  width: 700px;
}

.content-heading h3 {
  margin: 3px 0 0;

  color: var(--drawer-text);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 16px;
  line-height: 1.3;
}

.loading-badge {
  color: #4b7fc8;
}

.small-loader {
  width: 13px;
  height: 13px;

  border: 2px solid rgba(67, 136, 245, 0.18);
  border-top-color: #4388f5;
  border-radius: 50%;

  animation: spin 0.7s linear infinite;
}

/* =========================================================
   RESPONSES LAYOUT
========================================================= */

.responses-loading {
  min-height: 0;
  flex: 1;

  display: flex;
  flex-direction: column;
}

.responses-layout {
  min-height: 0;
  flex: 1;

  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: stretch;

  gap: 22px;

  overflow: hidden;

  transition:
    grid-template-columns 0.3s ease;
}

.responses-layout.has-details {
  grid-template-columns:
    minmax(500px, 1fr)
    minmax(360px, 410px);
}

.responses-column {
  min-width: 0;
  min-height: 0;

  overflow-y: auto;
  overflow-x: hidden;

  padding-right: 6px;

  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.responses-grid {
  display: grid;
  grid-template-columns:
    repeat(2, minmax(240px, 1fr));

  gap: 16px;

  align-content: start;
  margin-top: 24px;
}

.responses-grid > * {
  min-width: 0;
}

.responses-column::-webkit-scrollbar,
.drawer-content::-webkit-scrollbar,
.analysis-scroll::-webkit-scrollbar {
  width: 7px;
}

.responses-column::-webkit-scrollbar-track,
.drawer-content::-webkit-scrollbar-track,
.analysis-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.responses-column::-webkit-scrollbar-thumb,
.drawer-content::-webkit-scrollbar-thumb,
.analysis-scroll::-webkit-scrollbar-thumb {
  background: var(--drawer-scrollbar);

  border: 2px solid transparent;
  border-radius: 999px;

  background-clip: padding-box;
}

/* =========================================================
   DETAILS COLUMN
========================================================= */

.worker-details {
  min-width: 0;
  min-height: 0;
  height: 100%;

  display: flex;
  flex-direction: column;

  gap: 15px;

  overflow-y: auto;
  overflow-x: hidden;

  padding: 16px;

  background: var(--drawer-surface-soft);

  border: 1px solid var(--drawer-border-strong);
  border-radius: 20px;

  scrollbar-gutter: stable;
}

.details-header {
  min-height: 40px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;

  flex-shrink: 0;

  padding: 2px 2px 0;
}

.details-header h3 {
  margin: 3px 0 0;

  color: var(--drawer-text);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;
  line-height: 1.3;
}

.details-close {
  width: 31px;
  height: 31px;
}

.details-close svg {
  width: 16px;
  height: 16px;
}

/* =========================================================
   WORKER CARD
========================================================= */

.worker-card {
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;

  flex-shrink: 0;

  padding: 22px 18px 17px;

  text-align: center;

  background:
    radial-gradient(
      circle at 50% 0%,
      rgba(53, 132, 247, 0.11),
      transparent 165px
    ),
    var(--drawer-surface);

  border: 1px solid var(--drawer-border);
  border-radius: 18px;

  box-shadow:
    0 8px 24px rgba(15, 23, 42, 0.05);
}

[data-theme="dark"] .worker-card {
  box-shadow:
    0 10px 26px rgba(0, 0, 0, 0.18);
}

.avatar-wrapper {
  position: relative;

  width: 88px;
  height: 88px;

  margin-bottom: 12px;
}

.avatar {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  background: var(--drawer-surface-muted);

  border: 4px solid var(--drawer-surface);
  border-radius: 50%;

  box-shadow:
    0 9px 22px rgba(25, 47, 82, 0.15);
}

.status-dot {
  position: absolute;
  right: 2px;
  bottom: 4px;

  width: 16px;
  height: 16px;

  background: #32b56f;

  border: 3px solid var(--drawer-surface);
  border-radius: 50%;

  box-shadow:
    0 3px 8px rgba(38, 166, 98, 0.25);
}

.worker-name {
  margin: 0;

  color: var(--drawer-text);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 17px;
  line-height: 1.3;
}

.worker-title {
  margin: 5px 0 0;

  color: var(--drawer-text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.4;
}

.worker-meta {
  width: 100%;

  display: flex;
  justify-content: center;
  flex-wrap: wrap;

  gap: 7px;

  margin-top: 14px;
}

.worker-meta span {
  display: flex;
  align-items: center;
  gap: 5px;

  padding: 6px 8px;

  color: var(--drawer-text-secondary);
  background: var(--drawer-surface-soft);

  border: 1px solid var(--drawer-border);
  border-radius: 9px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.worker-meta svg {
  width: 13px;
  height: 13px;

  color: var(--drawer-blue);
}

/* =========================================================
   WORKER ACTIONS
========================================================= */

.action-buttons {
  width: 100%;

  display: flex;
  justify-content: center;
  gap: 8px;

  margin-top: 17px;
}

.primary-action,
.icon-action {
  min-height: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  border: none;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease,
    border-color 0.2s ease;
}

.primary-action {
  min-width: 0;
  flex: 1;

  gap: 7px;

  padding: 0 16px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3596f8,
      #3c75e6,
      #5368e7
    );

  border-radius: 13px;

  box-shadow:
    0 8px 18px rgba(48, 117, 224, 0.2);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

.primary-action img {
  width: 19px;
  height: 19px;

  object-fit: contain;

  filter: brightness(0) invert(1);
}

.icon-action {
  width: 42px;
  flex-shrink: 0;

  padding: 0;

  color: var(--drawer-blue);
  background: var(--drawer-surface-soft);

  border: 1px solid var(--drawer-border-strong);
  border-radius: 13px;
}

.icon-action img {
  width: 27px;
  height: 27px;

  object-fit: contain;
}

.icon-action svg {
  width: 20px;
  height: 20px;

  color: currentColor;

  transition:
    transform 0.2s ease;
}

.primary-action:hover,
.icon-action:hover {
  transform: translateY(-1px);
}

.primary-action:hover {
  box-shadow:
    0 11px 23px rgba(48, 117, 224, 0.27);
}

.icon-action:hover svg {
  transform: scale(1.08);
}

/* =========================================================
   AI ANALYSIS
========================================================= */

.ai-analysis {
  min-height:260px;
  flex: 1;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  padding: 17px;

  background:
    linear-gradient(
      145deg,
      rgba(53, 132, 247, 0.075),
      rgba(105, 82, 228, 0.04)
    ),
    var(--drawer-surface);

  border: 1px solid rgba(53, 132, 247, 0.15);
  border-radius: 18px;
}

.analysis-header {
  display: flex;
  align-items: center;
  gap: 10px;

  flex-shrink: 0;

  margin-bottom: 13px;
}

.analysis-icon {
  width: 36px;
  height: 36px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #3899f7,
      #6161df
    );

  border-radius: 11px;

  box-shadow:
    0 7px 15px rgba(60, 120, 224, 0.19);
}

.analysis-icon svg {
  width: 19px;
  height: 19px;
}

.analysis-header span {
  display: block;

  color: var(--drawer-text-muted);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.analysis-header h4 {
  margin: 2px 0 0;

  color: var(--drawer-text);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
  line-height: 1.3;
}

.analysis-scroll {
  min-height: 0;
  flex: 1;

  overflow-y: auto;
  overflow-x: hidden;

  padding-right: 8px;

  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.analysis-scroll p {
  margin: 0;

  color: var(--drawer-text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.72;

  white-space: pre-line;
  overflow-wrap: anywhere;
}

.openai-mark {
  display: flex;
  align-items: center;
  gap: 6px;

  flex-shrink: 0;

  margin-top: 14px;
  padding-top: 11px;

  color: var(--drawer-text-muted);

  border-top: 1px solid rgba(53, 132, 247, 0.12);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.openai-dot {
  width: 6px;
  height: 6px;

  background: #4388f5;
  border-radius: 50%;

  box-shadow:
    0 0 0 4px rgba(67, 136, 245, 0.1);
}

/* =========================================================
   EMPTY STATE
========================================================= */

.empty-responses {
  min-height: 0;
  flex: 1;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  padding: 40px;

  text-align: center;

  overflow-y: auto;
}

.empty-illustration {
  position: relative;

  width: 190px;
  height: 150px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-glow {
  position: absolute;

  width: 150px;
  height: 110px;

  background:
    radial-gradient(
      circle,
      rgba(58, 137, 247, 0.14),
      transparent 68%
    );

  filter: blur(5px);
}

.empty-illustration img {
  position: relative;

  max-width: 165px;
  max-height: 135px;

  object-fit: contain;
}

.empty-responses h3 {
  margin: 14px 0 0;

  color: var(--drawer-text);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
}

.empty-responses > p {
  max-width: 390px;

  margin: 8px 0 0;

  color: var(--drawer-text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.55;
}

.empty-note {
  display: flex;
  align-items: center;
  gap: 7px;

  margin-top: 20px;
  padding: 9px 11px;

  color: var(--drawer-text-secondary);
  background: rgba(53, 132, 247, 0.06);

  border: 1px solid rgba(53, 132, 247, 0.11);
  border-radius: 11px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.empty-note svg {
  width: 15px;
  height: 15px;

  flex-shrink: 0;

  color: #4388f5;
}

/* =========================================================
   FOOTER
========================================================= */

.drawer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  flex-shrink: 0;

  padding:
    14px
    22px
    calc(14px + env(safe-area-inset-bottom));

  background: color-mix(
    in srgb,
    var(--drawer-bg) 94%,
    transparent
  );

  border-top: 1px solid var(--drawer-border);

  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
}

.edit-btn,
.delete-btn {
  min-height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;

  padding: 0 18px;

  cursor: pointer;

  border-radius: 13px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease;
}

.edit-btn {
  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3596f8,
      #3e71e2
    );

  border: none;

  box-shadow:
    0 7px 16px rgba(48, 117, 224, 0.18);
}

.delete-btn {
  color: var(--drawer-danger);
  background: rgba(216, 79, 85, 0.08);

  border: 1px solid rgba(216, 79, 85, 0.14);
}

.edit-btn svg,
.delete-btn svg {
  width: 16px;
  height: 16px;
}

.edit-btn:hover,
.delete-btn:hover {
  transform: translateY(-1px);
}

.edit-btn:hover {
  box-shadow:
    0 10px 21px rgba(48, 117, 224, 0.24);
}

.delete-btn:hover {
  color: #ffffff;
  background: #d95359;

  box-shadow:
    0 7px 16px rgba(206, 67, 73, 0.18);
}

/* =========================================================
   TRANSITIONS
========================================================= */

.details-enter-active,
.details-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.26s cubic-bezier(0.22, 1, 0.36, 1);
}

.details-enter-from,
.details-leave-to {
  opacity: 0;
  transform: translateX(14px);
}

/* =========================================================
   ANIMATIONS
========================================================= */

@keyframes overlay-appear {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes drawer-appear {
  from {
    opacity: 0;
    transform: translateX(-28px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes drawer-mobile-appear {
  from {
    opacity: 0;
    transform: translateY(32px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* =========================================================
   TABLET
========================================================= */

@media (max-width: 1100px) {
  .drawer-panel.expanded {
    width: min(980px, calc(100vw - 24px));
  }

  .responses-layout.has-details {
    grid-template-columns:
      minmax(350px, 1fr)
      minmax(330px, 380px);
  }

  .responses-grid {
    grid-template-columns: 1fr;
  }
}

/* =========================================================
   MOBILE
========================================================= */

@media (max-width: 700px) {
  .drawer-overlay {
    align-items: flex-end;

    padding: 0;

    background: rgba(0, 0, 0, 0.5);
  }

  .drawer-panel,
  .drawer-panel.expanded {
    width: 100%;
    height: 96dvh;

    border-right: none;
    border-bottom: none;
    border-left: none;

    border-radius: 24px 24px 0 0;

    animation:
      drawer-mobile-appear
      0.3s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .drawer-header {
    min-height: 78px;

    padding: 14px 16px;
  }

  .header-logo {
    width: 42px;
    height: 42px;

    border-radius: 13px;
  }

  .header-logo img {
    width: 23px;
  }

  .header-content h2 {
    font-size: 15px;
  }

  .header-content p {
    max-width: calc(100vw - 150px);

    font-size: 10px;
  }

  .close-button {
    width: 35px;
    height: 35px;
  }

  .drawer-content {
    padding: 18px 14px;
  }

  .content-heading {
    align-items: flex-end;

    margin-bottom: 14px;
  }

  .content-heading h3 {
    font-size: 14px;
  }

  .responses-layout,
  .responses-layout.has-details {
    display: flex;
    flex-direction: column;

    overflow-y: auto;
  }

  .responses-column {
    flex-shrink: 0;

    overflow: visible;

    padding-right: 0;
  }

  .responses-grid {
    grid-template-columns: 1fr;
    gap: 13px;
  }

  .worker-details {
    min-width: 0;
    min-height: 0;
    height: 100%;

    display: flex;
    flex-direction: column;

    gap: 15px;

    overflow-y: auto;
    overflow-x: hidden;

    padding: 16px;

    scrollbar-gutter: stable;
  }

  .worker-card {
    padding: 20px 15px 16px;
  }

  .avatar-wrapper {
    width: 82px;
    height: 82px;
  }

  .worker-name {
    font-size: 16px;
  }

  .analysis-scroll {
    max-height: none;
  }

  .empty-responses {
    min-height: 60vh;

    padding: 30px 15px;
  }

  .empty-illustration {
    width: 155px;
    height: 125px;
  }

  .empty-illustration img {
    max-width: 140px;
    max-height: 115px;
  }

  .empty-responses h3 {
    font-size: 16px;
  }

  .drawer-footer {
    padding:
      12px
      14px
      calc(12px + env(safe-area-inset-bottom));
  }

  .edit-btn,
  .delete-btn {
    min-height: 46px;
    flex: 1;

    padding: 0 12px;
  }

  .details-enter-from,
  .details-leave-to {
    transform: translateY(10px);
  }
}

/* =========================================================
   REDUCED MOTION
========================================================= */

@media (prefers-reduced-motion: reduce) {
  .drawer-overlay,
  .drawer-panel,
  .small-loader {
    animation: none;
  }

  .drawer-panel,
  .close-button,
  .details-close,
  .primary-action,
  .icon-action,
  .edit-btn,
  .delete-btn,
  .details-enter-active,
  .details-leave-active {
    transition: none;
  }
}

.icon-action {
  width: 42px;
  height: 42px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  border: none;
  border-radius: 13px;

  background: linear-gradient(
    135deg,
    #3596f8,
    #3c75e6,
    #5368e7
  );

  box-shadow:
    0 8px 18px rgba(48, 117, 224, .2);

  transition:
    transform .2s ease,
    box-shadow .25s ease,
    filter .25s ease;
}


.icon-action:active {
  transform: translateY(0) scale(.96);
}

.icon-action:hover {
  transform: translateY(-2px);

  box-shadow:
    0 12px 24px rgba(48,117,224,.3);

  filter: brightness(1.08);
}

.icon-action img,
.icon-action svg {
  width: 20px;
  height: 20px;

  object-fit: contain;

  filter: brightness(0) invert(1);
}

.menu-wrapper{
    position:relative;
}

.action-menu {
    position:absolute;
    top:calc(100% + 10px);
    right:0;

    width:260px;

    background:rgba(35,35,39,.95);
    backdrop-filter:blur(18px);

    border:1px solid rgba(255,255,255,.08);
    border-radius:18px;

    padding:8px;

    box-shadow:
        0 25px 60px rgba(0,0,0,.45);

    z-index:50;
}

.action-menu button{
    width:100%;

    display:flex;
    align-items:center;

    gap:14px;

    padding:12px;

    border:none;
    background:none;

    border-radius:14px;

    cursor:pointer;

    transition:.2s;
}

.action-menu button:hover{
    background:rgba(61,130,237,.12);
}

.menu-icon{
    width:32px;
    height:32px;

    display:flex;
    align-items:center;
    justify-content:center;

    flex-shrink:0;

    border-radius:12px;

    background:linear-gradient(
        135deg,
        #3596f8,
        #3d75e7,
        #5966e7
    );

    box-shadow:
        0 8px 18px rgba(61,130,237,.25);
}

.menu-icon img{
    width:16px;
    filter:brightness(0) invert(1);
}

.action-menu span{
    display:block;

    color:white;

    font-family:"Eastman-Bold";

    font-size:13px;
}

.action-menu small{
    display:block;

    margin-top:2px;

    color:#9098a6;

    font-size:11px;
}

.divider{
    height:1px;

    margin:6px 8px;

    background:rgba(255,255,255,.07);
}

.danger:hover{
    background:rgba(230,80,80,.12)!important;
}

.danger-icon{
    background:
        linear-gradient(
            135deg,
            #ff6464,
            #db4545
        );
}

.menu-enter-active,
.menu-leave-active{
    transition:.18s;
}

.menu-enter-from,
.menu-leave-to{
    opacity:0;
    transform:translateY(-8px) scale(.95);
}

.menu-text{
    flex:1;

    display:flex;
    flex-direction:column;

    align-items:flex-start;
    justify-content:center;

    text-align:left;
}

/* скрываем scrollbar, но оставляем скролл */

.worker-details {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}*

.worker-details::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}

</style>